import { HttpErrorResponse } from '@angular/common/http';
import {
  FORM_FIELD_TYPES,
  FORM_LAYOUT_TYPES,
  FormField,
  FormFieldType,
  FormLayoutType,
  FormSchemaModel,
  FormSection,
  createEmptySchema,
  createLayoutForType,
  localizedText
} from '../../../shared/dynamic-form/form-schema.models';
import { CurrentContext } from '../../../core/context/current-context.model';
import {
  DynamicFieldSemanticType,
  DynamicFieldType,
  DYNAMIC_FIELD_TYPES
} from '../dynamic.models';
import {
  EntityDraft,
  FormDraft,
  FormFieldDraft
} from './ai-authoring.models';

export function hasAiGeneratePermission(context: CurrentContext | null): boolean {
  const roles = new Set(context?.roles ?? []);
  if (roles.has('SystemAdmin')) {
    return true;
  }

  const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.ai');
  if (!node) {
    return false;
  }

  const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
  return actions.has('generate');
}

export function extractAiAuthoringErrorCode(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }

  const body = error.error as Record<string, unknown> | null;
  if (!body) {
    return null;
  }

  if (typeof body['error'] === 'string') {
    return body['error'];
  }

  if (typeof body['Error'] === 'string') {
    return body['Error'];
  }

  return null;
}

export function resolveAiAuthoringError(
  error: unknown,
  translate: (key: string) => string
): string {
  const code = extractAiAuthoringErrorCode(error);
  if (code) {
    const key = `dynamic.aiAuthoring.errors.${code}`;
    const translated = translate(key);
    if (translated !== key) {
      return translated;
    }
  }

  if (error instanceof HttpErrorResponse && error.status === 429) {
    const fallback = translate('dynamic.aiAuthoring.errors.ai_authoring_rate_limit_exceeded');
    if (fallback !== 'dynamic.aiAuthoring.errors.ai_authoring_rate_limit_exceeded') {
      return fallback;
    }
  }

  return translate('dynamic.aiAuthoring.errors.generic');
}

export function normalizeEntityFieldType(value: string): DynamicFieldType {
  const normalized = value.trim();
  const match = DYNAMIC_FIELD_TYPES.find(
    item => item.toLowerCase() === normalized.toLowerCase()
  );
  return match ?? 'Text';
}

export function normalizeEntitySemanticType(value: string | null | undefined): DynamicFieldSemanticType | undefined {
  if (!value?.trim()) {
    return undefined;
  }
  const normalized = value.trim();
  const allowed: DynamicFieldSemanticType[] = [
    'Text', 'Category', 'Identifier', 'Measure', 'Money', 'Percentage',
    'Quantity', 'Date', 'Timestamp', 'Boolean', 'Email', 'Phone', 'Url', 'Geo'
  ];
  return allowed.find(item => item.toLowerCase() === normalized.toLowerCase());
}

export function normalizeFormFieldType(value: string): FormFieldType {
  const normalized = value.trim().toLowerCase();
  const match = FORM_FIELD_TYPES.find(item => item.value === normalized);
  return match?.value ?? 'text';
}

export function normalizeLayoutType(value: string): FormLayoutType {
  const normalized = value.trim();
  const match = FORM_LAYOUT_TYPES.find(item => item.value === normalized);
  return match?.value ?? 'singleColumn';
}

export function entityDraftToEditable(draft: EntityDraft): EntityDraft {
  return {
    name: draft.name,
    displayName: draft.displayName,
    description: draft.description ?? '',
    iconKey: draft.iconKey ?? '',
    fields: draft.fields.map(field => ({ ...field })),
    relationships: (draft.relationships ?? []).map(rel => ({
      ...rel,
      include: true
    })),
    indexes: (draft.indexes ?? []).map(index => ({
      ...index,
      include: true,
      columnsText: (index.columns ?? []).join(', ')
    }))
  };
}

export function formDraftToSchema(draft: FormDraft, culture: string): FormSchemaModel {
  const layoutType = normalizeLayoutType(draft.layoutType);
  const schema = createEmptySchema(layoutType, culture);

  if (draft.key?.trim()) {
    schema.key = draft.key.trim();
  }

  schema.title = localizedText(draft.title.trim(), culture);
  schema.targetEntityKey = draft.targetEntityKey?.trim() || undefined;
  schema.layout = createLayoutForType(layoutType);

  if (layoutType === 'wizard' && draft.wizard) {
    schema.layout.wizard = {
      oneQuestionPerStep: draft.wizard.oneQuestionPerStep ?? false,
      showProgress: draft.wizard.showProgress ?? true,
      allowBack: draft.wizard.allowBack ?? true,
      stepValidation: 'onNext'
    };
  }

  schema.sections = (draft.sections ?? []).map(section => ({
    key: section.key.trim(),
    title: localizedText(section.title.trim(), culture),
    fields: (section.fields ?? []).map(field => mapFormFieldDraft(field, culture))
  })).filter(section => section.key.length > 0);

  if (!schema.sections.length) {
    schema.sections = createEmptySchema(layoutType, culture).sections;
  }

  schema.submitActions = (draft.submitActions ?? []).map(action => ({
    type: action.actionType,
    targetEntityKey: action.targetEntityKey?.trim() || undefined,
    map: Object.fromEntries(
      (action.fieldMap ?? []).map(item => [item.formFieldKey, item.entityFieldKey])
    )
  }));

  return schema;
}

function mapFormFieldDraft(field: FormFieldDraft, culture: string): FormField {
  const type = normalizeFormFieldType(field.type);
  const mapped: FormField = {
    key: field.key.trim(),
    type,
    label: localizedText(field.label.trim(), culture),
    required: field.required
  };

  if (field.options?.length) {
    mapped.options = field.options.map((option, index) => ({
      value: option.value,
      label: localizedText(option.label, culture),
      order: index + 1,
      enabled: true
    }));
  }

  if (field.conditional?.fieldKey?.trim()) {
    mapped.conditional = {
      showWhen: {
        field: field.conditional.fieldKey.trim(),
        operator: field.conditional.operator,
        value: field.conditional.value ?? undefined
      }
    };
  }

  return mapped;
}

export function hasSchemaContent(schema: FormSchemaModel): boolean {
  return schema.sections.some(section => section.fields.length > 0)
    || Object.values(schema.title ?? {}).some(value => value.trim().length > 0);
}

export interface FormDraftPreviewFieldRow {
  key: string;
  type: string;
  label: string;
  required: boolean;
  optionsCount: number;
  sectionTitle: string;
}

export function flattenFormDraftFields(draft: FormDraft): FormDraftPreviewFieldRow[] {
  const rows: FormDraftPreviewFieldRow[] = [];
  for (const section of draft.sections ?? []) {
    for (const field of section.fields ?? []) {
      rows.push({
        key: field.key,
        type: field.type,
        label: field.label,
        required: field.required,
        optionsCount: field.options?.length ?? 0,
        sectionTitle: section.title
      });
    }
  }
  return rows;
}
